import { createAsyncThunk } from "@reduxjs/toolkit";
import * as API from "../../services/productTrackerService";
import { getUserInfo } from "../../services/usersService";

// get all tracked orders from SAPI
export const getAllTrackerOrders = createAsyncThunk(
  "productTracker/orders/tracker",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getAllProductTrackerOrders();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// get all tracker order details from SAPI
export const getTrackerOrderDetails = createAsyncThunk(
  "productTracker/orders/trackerplus",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.getProductTrackerDetails();
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
/* 
export const getMergedTrackerOrders = createAsyncThunk(
  "productTracker/orders/merged",
  async (_, { rejectWithValue }) => {
    try {
      // First API call (wait for it to complete)
      const ordersResponse = await API.getAllProductTrackerOrders();
      const orders = ordersResponse.data?.recordsets?.[0] || [];

      // Second API call (only after the first is done)
      const detailsResponse = await API.getProductTrackerDetails();
      const details = detailsResponse.data?.recordsets?.[0] || [];

      // Create a map from details using "KEY" for quick lookup
      const detailsMap = new Map(details.map((item: any) => [item.KEY, item]));

      // Merge the details into the orders
      const mergedOrders = orders.map((order: any) => {
        const mergedOrder = { ...order, ...(detailsMap.get(order.Key) || {}) };

        // Replace OrderNumber1 with PODoc if it's null
        if (!mergedOrder.OrderNumber1) {
          mergedOrder.OrderNumber1 = mergedOrder.PODoc;
        }

        // Progress calculation
        const TotalOrdered = mergedOrder.TotalOrdered ?? 0;
        const TotalShipped = mergedOrder.TotalShipped ?? 0;
        let progress = 0;

        if (TotalShipped === null) {
          progress = 0; // If shipped is null, progress should be 0
        } else if (TotalOrdered > 0) {
          progress = Math.min((TotalShipped / TotalOrdered) * 100, 100); // Convert to percentage
        }

        mergedOrder.progress = progress;
        function parseDDMMYYYY(dateStr: string) {
          const [day, month, year] = dateStr.split("/");
          return new Date(`${year}-${month}-${day}`); // Convert to 'YYYY-MM-DD'
        }

        let readyByDate: any = parseDDMMYYYY(mergedOrder.ETA);

        // Fallback to today's date if parsing fails
        if (isNaN(readyByDate)) {
          readyByDate = new Date();
        }

        readyByDate.setDate(readyByDate.getDate() + 7);

        // Format as 'DD/MM/YYYY'
        mergedOrder.ReadyByDate = readyByDate.toLocaleDateString("en-GB");

        return mergedOrder;
      });

      //  Sort by OrderNumber1 (descending)
      mergedOrders.sort((a, b) => {
        // Convert to number if needed, handle null/undefined safely
        const numA = parseFloat(a.OrderNumber1) || 0;
        const numB = parseFloat(b.OrderNumber1) || 0;
        return numB - numA;
      });

      return mergedOrders;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
*/

async function waitForMinTime<T>(
  promise: Promise<T>,
  delayMs: number
): Promise<T> {
  return Promise.all([
    promise,
    new Promise<void>((res) => setTimeout(res, delayMs)),
  ]).then(([result]) => result);
}

export const getMergedTrackerOrders = createAsyncThunk(
  "productTracker/orders/merged",
  async (_, { rejectWithValue }) => {
    try {
      const ordersResponse = await waitForMinTime(
        API.getAllProductTrackerOrders(),
        1000
      );
      const detailsResponse = await waitForMinTime(
        API.getProductTrackerDetails(),
        1000
      );

      const orders = ordersResponse.data?.recordsets?.[0] || [];

      const details = detailsResponse.data?.recordsets?.[0] || [];

      // Create a map from details using "KEY"
      const detailsMap = new Map(details.map((item: any) => [item.KEY, item]));

      // Merge the details into the orders
      const mergedOrders = orders.map((order: any) => {
        const mergedOrder = { ...order, ...(detailsMap.get(order.Key) || {}) };

        // Replace OrderNumber1 with PODoc if it's null
        if (!mergedOrder.OrderNumber1) {
          mergedOrder.OrderNumber1 = mergedOrder.PODoc;
        }

        // Progress calculation
        const TotalOrdered = mergedOrder.TotalOrdered ?? 0;
        const TotalShipped = mergedOrder.TotalShipped ?? 0;
        let progress = 0;

        if (TotalShipped === null) {
          progress = 0;
        } else if (TotalOrdered > 0) {
          progress = Math.min((TotalShipped / TotalOrdered) * 100, 100);
        }

        mergedOrder.progress = progress;

        // Parse DD/MM/YYYY safely
        function parseDDMMYYYY(dateStr: string): Date | null {
          const [day, month, year] = dateStr.split("/").map(Number);

          if (!day || !month || !year) return null;

          // Month is zero-based (0 = Jan, 11 = Dec)
          return new Date(year, month - 1, day);
        }

        let readyByDate: any = parseDDMMYYYY(mergedOrder.RequestDate);
        if (isNaN(readyByDate)) {
          readyByDate = "";
        }

        if (readyByDate && readyByDate != "") {
          readyByDate.setDate(readyByDate.getDate() + 7);
        }

        mergedOrder.ReadyByDate =
          readyByDate && readyByDate != ""
            ? readyByDate.toLocaleDateString("en-GB")
            : "";

        return mergedOrder;
      });

      // Group orders by Key
      const groupedMap = new Map();

      for (const order of mergedOrders) {
        const key = order.Key?.trim();

        const totalOrdered = parseFloat(order.TotalOrdered) || 0;
        const shippedQty = parseFloat(order.ShippedQty) || 0;

        if (!groupedMap.has(key)) {
          groupedMap.set(key, {
            ...order,
            OrderQty: totalOrdered,
            TotalShipped: shippedQty,
            OrdersMerged: [order], // preserve details
          });
        } else {
          const group = groupedMap.get(key);
          group.OrderQty += totalOrdered;
          group.TotalShipped += shippedQty;
          group.OrdersMerged.push(order);
        }
      }

      // Compute RemainingToShip for merged groups
      const mergedAndCalculatedOrders = Array.from(groupedMap.values()).map(
        (order) => {
          order.RemainingToShip = order.OrderQty - order.TotalShipped;
          return order;
        }
      );

      // Sort by OrderNumber1 descending
      mergedAndCalculatedOrders.sort((a, b) => {
        const numA = parseFloat(a.OrderNumber1) || 0;
        const numB = parseFloat(b.OrderNumber1) || 0;
        return numB - numA;
      });

      return mergedAndCalculatedOrders;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// get all tracker enquiries by key
export const getTrackerEnquiriesByKey = createAsyncThunk(
  "productTracker/orders/enquiries",
  async (key: string, { rejectWithValue }) => {
    try {
      const { data } = await API.getTrackerEnquiriesByKey(key);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const searchTrackerEnquiriesByKey = createAsyncThunk(
  "productTracker/orders/enquiries/search",
  async ({ key, search }: any, { rejectWithValue }) => {
    try {
      const { data } = await API.searchTrackerEnquiriesByKey(key, search);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addTrackerOrderEnquiry = createAsyncThunk(
  "productTracker/orders/enquiries/add",
  async (payload: any, { rejectWithValue }) => {
    try {
      const userInfo = await getUserInfo();

      var httpBody = {
        createdBy: `${userInfo.given_name} ${userInfo.family_name}`,
        ...payload,
      };
      const { data } = await API.addTrackerEnquiry(httpBody);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
